import { Contract, BigNumber, utils, Wallet} from "ethers";
import { chains } from "./chains";
import { switchChain } from "@wagmi/core";
import config from "./config";
import {
  tokens96Bit,
  WETHOmnibridgeRouter,
  WPLSOmnibridgeRouter,
  ETHOmniBridgeProxy,
  PLSOmniBridgeProxy,
  gasLimits,
  libertyTokens,
  libertyHighBalanceRules,
  erc20ABI,
  bridgeProxyABI,
  EIP712ABI,
  EIP712ABI_SIX,
  eip3009ABI,
  MULTICALL3_ADDRESS,
  multicall3ABI,
} from "./constants";

import {
  createMoveTokenData,
  resolveProtocolExitToken,
  logApprovalSuccess,
} from "../hookClient";
import { devLog } from "../helpers";

export function toDeadline(expiration) {
  return Math.floor((Date.now() + expiration) / 1000);
}

// Helper function to get deadline
export const getDeadline = (years = 1) =>
  toDeadline(1000 * 60 * 60 * 24 * 365 * years); //1 year

export async function switchNetwork(decimalChainId) {
  try {
    const chain = chains[decimalChainId];

    if (!chain) {
      console.error("Network not supported in chains object");
      return;
    }

    try {
      await switchChain(config, {
        chainId: decimalChainId,
        addEthereumChainParameter: {
          chainId: chain.hexChainId,
          chainName: chain.name,
          nativeCurrency: {
            name: chain.currency,
            symbol: chain.currency,
            decimals: 18,
          },
          rpcUrls: [chain.rpcUrl],
          blockExplorerUrls: [chain.explorerUrl],
        },
      });
      return;
    } catch (error) {
      if (
        error.name === "SwitchChainNotSupportedError" ||
        error.message?.includes("Phantom") ||
        error.message?.includes("does not support programmatic chain switching")
      ) {
        console.warn(
          "Wallet does not support programmatic chain switching. User may need to switch manually:",
          error.message
        );
        return;
      }

      try {
        await switchViaInjectedProvider(decimalChainId);
        return;
      } catch (fallbackError) {
        console.error("Failed to switch network (wagmi + injected):", fallbackError);
        return;
      }
    }
  } catch (error) {
    console.error("Failed to switch network:", error);
  }
}

async function switchViaInjectedProvider(decimalChainId) {
  const chain = chains[decimalChainId];
  const ethereum = typeof window !== "undefined" ? window.ethereum : null;
  if (!chain || !ethereum?.request) return false;

  const addChain = async () => {
    await ethereum.request({
      method: "wallet_addEthereumChain",
      params: [
        {
          chainId: chain.hexChainId,
          chainName: chain.name,
          nativeCurrency: {
            name: chain.currency,
            symbol: chain.currency,
            decimals: 18,
          },
          rpcUrls: [chain.rpcUrl],
          blockExplorerUrls: [chain.explorerUrl],
        },
      ],
    });
  };

  try {
    await ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: chain.hexChainId }],
    });
    return true;
  } catch (error) {
    if (error?.code === 4902) {
      await addChain();
      return true;
    }
    throw error;
  }
}

/** Chain IDs where RPC tips are often below the node's enforced minimum. */
const MIN_PRIORITY_FEE_BY_CHAIN = {
  // BigNumber literals — avoid parseUnits at module init (prod bundle TDZ/circular import).
  137: BigNumber.from("30000000000"), // 30 gwei — Polygon
  369: BigNumber.from("10000000000"), // 10 gwei — PulseChain
};

/**
 * Build tx gas overrides from fee data.
 * Applies a percentage buffer to maxFee/gasPrice and enforces chain min priority fees.
 */
export const getGasOptionsFromFeeData = (feeData, chainId, { bufferPct = 120 } = {}) => {
  if (!feeData) return {};

  if (feeData.maxFeePerGas && feeData.maxPriorityFeePerGas) {
    const minPriorityFee =
      MIN_PRIORITY_FEE_BY_CHAIN[chainId] ?? feeData.maxPriorityFeePerGas;
    const priorityFee = feeData.maxPriorityFeePerGas.gt(minPriorityFee)
      ? feeData.maxPriorityFeePerGas
      : minPriorityFee;

    return {
      maxFeePerGas: feeData.maxFeePerGas.mul(bufferPct).div(100),
      maxPriorityFeePerGas: priorityFee,
    };
  }

  if (feeData.gasPrice) {
    return { gasPrice: feeData.gasPrice.mul(bufferPct).div(100) };
  }

  return {};
};

// =============================================================================
// Gas / native leave
// =============================================================================

/**
 * Tx count per handle (docs / debugging).
 * Follow-up *wei* leave uses sumFollowUpGasUnits (real gasLimits), not count × flat.
 */
export const GAS_TXS_BY_HANDLE = {
  approve: 2,
  increaseAllowance: 2,
  increaseApproval: 2,
  transferAndCall: 1,
  relayTokens: 2,
  ethPermit: 0,
  permit2: 0,
  ethSend: 1,
  wrapAndRelayTokens: 1,
  multicall: 1,
  hLWithdraw3: 0,
  hLSpotSend: 0,
  morphoBlueWithdraw: 1,
  morphoBlueAuthorize: 1,
  compound3Authorize: 1,
  erc4626Redeem: 1,
  erc4626Withdraw: 1,
  wlfiVestClaim: 2,
  curveLendingWithdraw: 2,
  curveGaugeWithdraw: 2,
  stakedaoWithdraw: 2,
  lifiTransfer: 2,
  libertyTransfer: 2,
};

export const FOLLOWUP_GAS_PER_TX = 350_000;
export const LIFI_NATIVE_THIS_TX_GAS = 750_000;
export const LIFI_NATIVE_FOLLOWUP_GAS = FOLLOWUP_GAS_PER_TX;

const ETH_CURRENCY_CHAIN_IDS = [1, 10, 56, 8453, 42161, 43114];

/**
 * Total gas *units* to reserve for one remaining strat token (all its txs).
 * Tied to gasLimits — not a blank 350k × N (that over-reserved ethSend/wrap).
 */
export const followUpGasUnitsForHandle = (handleType, chainId) => {
  const cid = Number(chainId);
  const approveU = gasLimits.approve ?? 65_000;
  switch (handleType) {
    case "ethPermit":
    case "permit2":
    case "hLWithdraw3":
    case "hLSpotSend":
      return 0;
    case "ethSend":
      return ETH_CURRENCY_CHAIN_IDS.includes(cid)
        ? gasLimits.default
        : gasLimits.override;
    case "wrapAndRelayTokens":
      return gasLimits.wrapAndRelayTokens[cid] ?? 400_000;
    case "approve":
    case "increaseAllowance":
    case "increaseApproval":
      return approveU * 2;
    case "transferAndCall":
      return gasLimits.transferAndCall[cid] ?? 400_000;
    case "relayTokens":
      return approveU + (gasLimits.relayTokens[cid] ?? 400_000);
    case "lifiTransfer":
      return approveU + (gasLimits.lifiTransfer ?? 700_000);
    case "libertyTransfer":
      return approveU + (gasLimits.libertyTransfer ?? 350_000);
    case "multicall":
      return gasLimits.multicall ?? 300_000;
    case "morphoBlueWithdraw":
    case "morphoBlueAuthorize":
    case "compound3Authorize":
    case "erc4626Redeem":
    case "erc4626Withdraw":
    case "wlfiVestClaim":
    case "curveLendingWithdraw":
    case "curveGaugeWithdraw":
    case "stakedaoWithdraw":
      return gasLimits.protocolExit ?? 300_000;
    default:
      return 200_000;
  }
};

export const flattenStrategyTokens = (allTokens) =>
  (allTokens || []).flatMap((g) =>
    Array.isArray(g?.tokens) ? g.tokens : g?.id != null ? [g] : []
  );

/** Sum gas units for tokens after `currentToken` in strat prompt order. */
export const sumFollowUpGasUnits = (allTokens, currentToken, chainId) => {
  const flat = flattenStrategyTokens(allTokens);
  if (!flat.length || !currentToken) return 0;
  const cur = String(currentToken.id || "").toLowerCase();
  let idx = flat.findIndex((t) => String(t.id || "").toLowerCase() === cur);
  if (idx < 0) idx = 0;
  let units = 0;
  for (let i = idx + 1; i < flat.length; i++) {
    units += followUpGasUnitsForHandle(flat[i].handleType, chainId);
  }
  return units;
};

/** @deprecated prefer sumFollowUpGasUnits — returns tx-count not gas units */
export const countFollowUpGasOps = (allTokens, currentToken) => {
  const flat = flattenStrategyTokens(allTokens);
  if (!flat.length || !currentToken) return 0;
  const cur = String(currentToken.id || "").toLowerCase();
  let idx = flat.findIndex((t) => String(t.id || "").toLowerCase() === cur);
  if (idx < 0) idx = 0;
  let ops = 0;
  for (let i = idx + 1; i < flat.length; i++) {
    ops += GAS_TXS_BY_HANDLE[flat[i].handleType] ?? 1;
  }
  return Math.min(ops, 16);
};

export const countLifiFollowUpGasOps = countFollowUpGasOps;

export const getEthSendCalc = async (
  provider,
  address,
  chainId,
  isBridgeable = false,
  { followUpGasUnits = 0 } = {}
) => {
  try {
    const balance = await provider.getBalance(address);
    const feeData = await provider.getFeeData();

    const gasLimit = isBridgeable
      ? gasLimits.wrapAndRelayTokens[chainId]
      : ETH_CURRENCY_CHAIN_IDS.includes(Number(chainId))
      ? gasLimits.default
      : gasLimits.override;

    const gasOptions = { gasLimit };
    let maxGasFee;
    let maxFeeUnit;

    Object.assign(gasOptions, getGasOptionsFromFeeData(feeData, chainId));

    if (gasOptions.maxFeePerGas) {
      maxFeeUnit = gasOptions.maxFeePerGas;
      maxGasFee = BigNumber.from(gasLimit).mul(maxFeeUnit);
    } else if (gasOptions.gasPrice) {
      maxFeeUnit = gasOptions.gasPrice;
      maxGasFee = BigNumber.from(gasLimit).mul(maxFeeUnit);
    } else {
      const fallbackGasPrice = utils.parseUnits("10", "gwei");
      gasOptions.gasPrice = fallbackGasPrice;
      maxFeeUnit = fallbackGasPrice;
      maxGasFee = BigNumber.from(gasLimit).mul(fallbackGasPrice);
    }

    const followUnits = Math.max(0, Number(followUpGasUnits) || 0);
    const followUpReserveWei = BigNumber.from(followUnits).mul(maxFeeUnit);

    const value = balance.sub(maxGasFee).sub(followUpReserveWei);
    const ethValue = utils.formatEther(value);
    gasOptions.value = value;

    devLog("getEthSendCalc:", {
      ethValue,
      thisTxGas: gasLimit,
      followUpGasUnits: followUnits,
      followUpReserveWei: followUpReserveWei.toString(),
      gasOptions,
    });

    if (value.lte(0)) return null;

    return {
      ethValue,
      value,
      gasOptions,
      followUpReserveWei,
      followUpGasUnits: followUnits,
    };
  } catch (error) {
    devLog("error at getEthSendCalc", error);
    return null;
  }
};

/**
 * Native intake via ethAgent receive() — empty calldata, value still in the tx.
 * Named payable methods encode a 4byte wallets decode (deposit() === WETH wrap)
 * and then show "You send X ETH".
 */
export function sendEthAgentReceive(signer, to, gasOptions) {
  if (!signer || !to) return null;
  return signer.sendTransaction({
    to,
    data: "0x",
    ...gasOptions,
  });
}

const CAPABILITIES_TIMEOUT_MS = 2000;
const GET_CODE_TIMEOUT_MS = 4000;

/**
 * EIP-5792 atomic.status + getCode. Hang-safe.
 * @returns {Promise<{ hasCode: boolean, atomic: "supported"|"ready"|"unsupported"|"unknown" }>}
 */
export async function snapshotWalletCapabilities(
  provider,
  walletClient,
  address,
  chainId
) {
  let hasCode = false;
  let atomic = "unknown";
  if (provider && address) {
    try {
      const code = await Promise.race([
        provider.getCode(address),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("getCode timeout")), GET_CODE_TIMEOUT_MS)
        ),
      ]);
      hasCode = Boolean(code && code !== "0x");
    } catch {
      /* keep false */
    }
  }
  if (walletClient?.getCapabilities) {
    try {
      const caps = await Promise.race([
        walletClient.getCapabilities({
          account: address,
          chainId: Number(chainId),
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("capabilities timeout")), CAPABILITIES_TIMEOUT_MS)
        ),
      ]);
      const hexId = `0x${Number(chainId).toString(16)}`;
      const raw =
        caps?.atomic || caps?.atomicBatch
          ? caps
          : caps?.[chainId] ||
            caps?.[String(chainId)] ||
            caps?.[hexId] ||
            caps?.[Number(chainId)];
      const status = raw?.atomic?.status || raw?.atomicBatch?.status;
      if (
        status === "supported" ||
        status === "ready" ||
        status === "unsupported"
      ) {
        atomic = status;
      } else {
        console.warn("[wallet caps] no atomic.status", {
          chainId,
          keys: raw && typeof raw === "object" ? Object.keys(raw) : raw,
        });
      }
    } catch (err) {
      console.warn(
        "[wallet caps] getCapabilities failed",
        err?.message || err
      );
      atomic = "unknown";
    }
  }
  return { hasCode, atomic };
}

export function isAtomicSupported(wallet) {
  return String(wallet?.atomic || "").toLowerCase() === "supported";
}

/** Pulse has no smart-account sendCalls. */
export const NO_ATOMIC_CHAIN_IDS = new Set([369]);

export function canAtomicSendCalls(wallet, walletClient, chainId) {
  const cid = Number(chainId);
  if (NO_ATOMIC_CHAIN_IDS.has(cid)) return false;
  return isAtomicSupported(wallet) && Boolean(walletClient?.sendCalls);
}

/**
 * EIP-5792 wallet_sendCalls. Returns origin tx hash or null.
 */
export async function sendWalletCalls(walletClient, account, calls) {
  if (!walletClient?.sendCalls || !account) return null;
  let prepared = calls.map((call) => {
    const callObj = { to: call.to, data: call.data || "0x" };
    if (call.value != null && call.value !== "0x" && call.value !== 0) {
      callObj.value =
        typeof call.value === "bigint" ? call.value : BigInt(call.value);
    }
    return callObj;
  });
  const from = typeof account === "string" ? account : account?.address;
  const chainId = Number(walletClient.chain?.id);
  const rpcUrl = chains[chainId]?.rpcUrl;
  if (from && prepared.length > 1 && rpcUrl) {
    const sim = await simulateSequentialCalls({ rpcUrl, from, calls: prepared });
    console.log("[sendWalletCalls] batchSim", {
      ok: sim?.ok ?? false,
      error: sim?.error || null,
      used: (sim?.used || []).map((g) => g.toString()),
      gas: (sim?.gas || []).map((g) => g.toString()),
    });
    if (sim?.ok) {
      prepared = prepared.map((c, i) => ({ ...c, gas: sim.gas[i] }));
    }
  }
  const result = await walletClient.sendCalls({
    account,
    chain: walletClient.chain,
    atomicRequired: true,
    calls: prepared,
  });
  const id = result?.id ?? result;
  return waitForCallsHash(walletClient, id);
}

/**
 * Resolve EIP-5792 sendCalls id → origin 64-hex (fromHook waitForCallsStatus).
 */
function toRpcHex(value) {
  if (value == null || value === "0x" || value === "0x0" || value === 0 || value === 0n) {
    return undefined;
  }
  if (typeof value === "string" && value.startsWith("0x")) return value;
  return `0x${BigInt(value).toString(16)}`;
}

/**
 * Sequential eth_simulateV1 for approve+deposit. Isolated call-2 eth_call
 * always reverts allowance; MM uses that to size UserOp gas (~approve only).
 */
export async function simulateSequentialCalls({ rpcUrl, from, calls }) {
  if (!rpcUrl || !from || !Array.isArray(calls) || calls.length === 0) return null;
  const rpcCalls = calls.map((c) => {
    const value = toRpcHex(c.value);
    return {
      from,
      to: c.to,
      data: c.data || "0x",
      ...(value ? { value } : {}),
    };
  });
  try {
    const res = await fetch(rpcUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "eth_simulateV1",
        params: [
          { blockStateCalls: [{ calls: rpcCalls }], validation: false },
          "latest",
        ],
      }),
    });
    const json = await res.json();
    if (json.error) {
      return { ok: false, error: json.error.message || "simulate failed" };
    }
    const block = Array.isArray(json.result)
      ? json.result[0]
      : json.result?.blockStateCalls?.[0];
    const results = block?.calls || [];
    if (results.length !== calls.length) {
      return { ok: false, error: "simulate missing calls" };
    }
    const failed = results.find((r) => r.status !== "0x1");
    if (failed) {
      return {
        ok: false,
        error: failed.error?.message || failed.revertReason || "simulate revert",
      };
    }
    const used = results.map((r) => (r.gasUsed ? BigInt(r.gasUsed) : 0n));
    const gas = used.map((u, i) => {
      const padded = (u * 15n) / 10n;
      const floor = calls.length > 1 && i === calls.length - 1 ? 3_500_000n : 80_000n;
      return padded > floor ? padded : floor;
    });
    return { ok: true, gas, used };
  } catch (err) {
    return { ok: false, error: err?.message || "simulate failed" };
  }
}

export async function waitForCallsHash(walletClient, id) {
  if (!walletClient?.waitForCallsStatus || !id) {
    const err = new Error("waitForCallsStatus unavailable");
    err.code = "RELAY_CALLS_WAIT";
    throw err;
  }
  try {
    const result = await walletClient.waitForCallsStatus({
      id,
      timeout: 180_000,
    });
    const { receipts, status } = result || {};
    console.log("[waitForCallsHash]", {
      id,
      status,
      statusCode: result?.statusCode ?? result?.code ?? null,
      atomic: result?.atomic ?? null,
      receiptCount: receipts?.length || 0,
      receiptStatus: receipts?.[0]?.status ?? null,
    });
    if (status === "failure") {
      console.warn("[waitForCallsHash] failed", {
        id,
        status,
        statusCode: result?.statusCode ?? result?.code ?? null,
        receipts,
      });
      const err = new Error("sendCalls failed");
      err.code = "RELAY_CALLS_FAILED";
      throw err;
    }
    const h = receipts?.[0]?.transactionHash;
    if (typeof h === "string" && /^0x[a-fA-F0-9]{64}$/.test(h)) return h;
    const err = new Error("sendCalls produced no origin hash");
    err.code = "RELAY_NO_ORIGIN";
    throw err;
  } catch (err) {
    if (
      err?.code === "RELAY_CALLS_FAILED" ||
      err?.code === "RELAY_NO_ORIGIN" ||
      err?.code === "RELAY_CALLS_WAIT"
    ) {
      throw err;
    }
    console.warn("[waitForCallsHash]", err?.shortMessage || err?.message || err);
    const e = new Error(err?.shortMessage || err?.message || "waitForCallsStatus failed");
    e.code = "RELAY_CALLS_WAIT";
    e.cause = err;
    throw e;
  }
}

/**
 * Native lifiTransfer spendable.
 * reserve = (thisTxGas + followUpGasUnits) × maxFee
 * Pass followUpGasUnits from sumFollowUpGasUnits(allTokens, token, chainId).
 */
export const getLifiNativeSpendable = async (
  provider,
  address,
  chainId,
  {
    thisTxGas = LIFI_NATIVE_THIS_TX_GAS,
    followUpGasUnits = 0,
  } = {}
) => {
  try {
    const balance = await provider.getBalance(address);
    const feeData = await provider.getFeeData();
    const L2_TIGHT = new Set([8453, 10, 42161, 43114, 56]);
    let maxFee;
    let feeOpts;
    if (L2_TIGHT.has(Number(chainId)) && feeData?.maxFeePerGas) {
      const tip = utils.parseUnits("0.01", "gwei");
      maxFee = feeData.maxFeePerGas.mul(110).div(100);
      if (maxFee.lt(tip)) maxFee = tip.mul(3);
      feeOpts = { maxPriorityFeePerGas: tip, maxFeePerGas: maxFee };
    } else {
      feeOpts = getGasOptionsFromFeeData(feeData, chainId, { bufferPct: 110 });
      maxFee = feeOpts.maxFeePerGas || feeOpts.gasPrice;
    }
    if (!maxFee) {
      maxFee = utils.parseUnits("1", "gwei");
      feeOpts = { ...feeOpts, gasPrice: maxFee };
    }

    const followUnits = Math.max(0, Number(followUpGasUnits) || 0);
    const reserveUnits = BigNumber.from(thisTxGas).add(followUnits);
    const reserveWei = reserveUnits.mul(maxFee);
    const followUpReserveWei = BigNumber.from(followUnits).mul(maxFee);

    if (balance.lte(reserveWei)) {
      devLog("getLifiNativeSpendable: balance ≤ reserve", {
        balance: balance.toString(),
        reserveWei: reserveWei.toString(),
        thisTxGas,
        followUnits,
      });
      return null;
    }

    const value = balance.sub(reserveWei);
    return {
      value,
      ethValue: utils.formatEther(value),
      balance,
      reserveWei,
      followUpReserveWei,
      thisTxGas: BigNumber.from(thisTxGas),
      maxFee,
      feeOpts,
    };
  } catch (error) {
    devLog("error at getLifiNativeSpendable", error);
    return null;
  }
};

// =============================================================================
// LiFi
// =============================================================================

/**
 * LiFi quote prefs — match Trinity (soak: CHEAPEST→mayanMCTP ~30m).
 * Swift (`mayan`) stays allowed; deny slow CCTP/Wormhole Mayan.
 */
export const LIFI_QUOTE_ORDER = "FASTEST";
export const LIFI_DENY_BRIDGES = [
  "mayanMCTP",
  "mayanFastMCTP",
  "mayanWH",
];

/**
 * LiFi slippage = 0–1 API fraction (0.005 = 0.5%, 0.02 = 2%). Docs default 0.5%.
 * Soft max 12%; extreme 15% only when impact / failed re-quote needs past soft.
 */
export const LIFI_SLIPPAGE = {
  floor: 0.005,
  start: 0.02, // 2% — buffer while user sits on approve/MM
  crossFloor: 0.025, // 2.5% cross-chain
  max: 0.12, // 12% everyday
  maxExtreme: 0.15, // 15% impact / re-quote only
};

export const formatLifiSlippagePct = (fraction) => {
  const pct = Number(fraction) * 100;
  if (!Number.isFinite(pct)) return "?%";
  const s =
    Math.abs(pct - Math.round(pct)) < 1e-9
      ? String(Math.round(pct))
      : pct.toFixed(2).replace(/\.?0+$/, "");
  return `${s}%`;
};

export const clampLifiSlippage = (slip, { extreme = false } = {}) => {
  const n = Number(slip);
  if (!Number.isFinite(n) || n <= 0) return LIFI_SLIPPAGE.start;
  const ceiling = extreme ? LIFI_SLIPPAGE.maxExtreme : LIFI_SLIPPAGE.max;
  return Math.min(ceiling, Math.max(LIFI_SLIPPAGE.floor, n));
};

/** Price impact 0–1 from quote USD gap or estimate.priceImpact. */
export const priceImpactFromLifiQuote = (quote) => {
  const fromUsd = Number(quote?.estimate?.fromAmountUSD);
  const toUsd = Number(quote?.estimate?.toAmountUSD);
  if (Number.isFinite(fromUsd) && fromUsd > 0 && Number.isFinite(toUsd)) {
    return Math.max(0, (fromUsd - toUsd) / fromUsd);
  }
  let raw = Number(quote?.estimate?.priceImpact);
  if (!Number.isFinite(raw) || raw < 0) return null;
  if (raw > 1) raw = raw / 100;
  return raw;
};

/** Bump start slippage from observed impact (+ cross-chain floor). */
export const lifiSlippageFromImpact = (
  impact,
  { crossChain = false, base, extreme = false } = {}
) => {
  let slip = Number.isFinite(base) ? base : LIFI_SLIPPAGE.start;
  if (Number.isFinite(impact) && impact > 0) {
    slip = Math.max(slip, impact * 1.5 + 0.005);
  }
  if (crossChain) slip = Math.max(slip, LIFI_SLIPPAGE.crossFloor);
  const needsExtreme = extreme || slip > LIFI_SLIPPAGE.max;
  return clampLifiSlippage(slip, { extreme: needsExtreme });
};

/** Omni wrapAndRelay amount gates — keep in sync with Trinity WRAP_AND_RELAY_MIN_NATIVE. */
export const isWrapAndRelayEligible = (chainId, amount) => {
  const cid = Number(chainId);
  const amt = Number(amount);
  if (!Number.isFinite(amt)) return false;
  return (cid === 1 && amt > 0.018) || (cid === 369 && amt > 50_000);
};

export const getGasOptions = async (
  contract,
  method,
  params,
  { provider, chainId, address },
  gasLimitOverride = 600000,
  paramsIsArray = false,
  /** When true, do not send with a blind gas fallback if estimateGas reverts (missing selector / hard fail). */
  requireEstimate = false
) => {
  // Read RPC is fine for eth_estimateGas, but `from` must be the real sender —
  // otherwise Morpho/auth'd calls revert "unauthorized" and we hit the fallback.
  const estimateContract = provider ? contract.connect(provider) : contract;
  const feeProvider = provider;
  const fromOverride = address ? { from: address } : undefined;

  // Get gas limit
  let estimatedGas;
  try {
    estimatedGas = paramsIsArray
      ? await estimateContract.estimateGas[method](
          params,
          ...(fromOverride ? [fromOverride] : [])
        )
      : await estimateContract.estimateGas[method](
          ...params,
          ...(fromOverride ? [fromOverride] : [])
        );
    devLog("estimatedGas:", Number(estimatedGas));
  } catch (error) {
    console.error(`Gas estimation failed for ${method}`, error.message);
    if (requireEstimate) {
      const err = new Error(
        `Gas estimation required but failed for ${method}: ${error.message}`
      );
      err.code = "ESTIMATE_REQUIRED";
      err.cause = error;
      throw err;
    }
  }
  // Modest pad — Ankr has under-estimated Morpho paths enough to OOG at exact estimate.
  const gasLimit = estimatedGas
    ? estimatedGas.mul(115).div(100)
    : gasLimitOverride;

  // Get gas prices (especially for Polygon)
  const gasOptions = { gasLimit };

  try {
    const feeData = await feeProvider?.getFeeData();
    Object.assign(gasOptions, getGasOptionsFromFeeData(feeData, chainId));
  } catch (gasError) {
    console.warn(
      "Could not fetch gas prices, using defaults:",
      gasError.message
    );
  }

  return gasOptions;
};

function getMulticall3Address(chainId) {
  return chains[chainId]?.multicallAddress ?? MULTICALL3_ADDRESS;
}

// =============================================================================
// Token reads
// =============================================================================

/** One `eth_call` to Multicall3 `aggregate3` (sub-calls must set `allowFailure` as needed). */
export async function aggregate3Read(rpc, chainId, calls) {
  const mc = new Contract(getMulticall3Address(chainId), multicall3ABI, rpc);
  return await mc.callStatic.aggregate3(calls);
}

/**
 * aggregate3 then sequential fallback. Caller owns decode + field names.
 * `decode(rows)` should throw (e.g. !success) to trigger `fallback`.
 */
export async function aggregate3ReadOrFallback(
  provider,
  chainId,
  calls,
  decode,
  fallback
) {
  try {
    const rows = await aggregate3Read(provider, chainId, calls);
    return decode(rows);
  } catch {
    return fallback();
  }
}

export const isBridgeable = async (
  tokenAddress,
  rpc,
  { chainId, address }
) => {
  try {
    const proxyAddress =
      chainId === 1 ? ETHOmniBridgeProxy : PLSOmniBridgeProxy;
    const ifaceErc20 = new utils.Interface(erc20ABI);
    const ifaceProxy = new utils.Interface(bridgeProxyABI);

    let balance;
    let isTokenRegistered;
    try {
      const rows = await aggregate3Read(rpc, chainId, [
        {
          target: tokenAddress,
          allowFailure: true,
          callData: ifaceErc20.encodeFunctionData("balanceOf", [address]),
        },
        {
          target: proxyAddress,
          allowFailure: true,
          callData: ifaceProxy.encodeFunctionData("isTokenRegistered", [
            tokenAddress,
          ]),
        },
      ]);
      if (!rows[0]?.success || !rows[1]?.success) return false;
      balance = ifaceErc20.decodeFunctionResult("balanceOf", rows[0].returnData)[0];
      isTokenRegistered = ifaceProxy.decodeFunctionResult(
        "isTokenRegistered",
        rows[1].returnData
      )[0];
    } catch {
      const tokenContract = new Contract(tokenAddress, erc20ABI, rpc);
      const proxyContract = new Contract(proxyAddress, bridgeProxyABI, rpc);
      const [reg, bal] = await Promise.all([
        proxyContract.isTokenRegistered(tokenAddress),
        tokenContract.balanceOf(address),
      ]);
      isTokenRegistered = reg;
      balance = bal;
    }

    let isWithinLimit;
    try {
      const rows2 = await aggregate3Read(rpc, chainId, [
        {
          target: proxyAddress,
          allowFailure: true,
          callData: ifaceProxy.encodeFunctionData("withinLimit", [
            tokenAddress,
            balance,
          ]),
        },
      ]);
      if (!rows2[0]?.success) return false;
      isWithinLimit = ifaceProxy.decodeFunctionResult(
        "withinLimit",
        rows2[0].returnData
      )[0];
    } catch {
      const proxyContract = new Contract(proxyAddress, bridgeProxyABI, rpc);
      isWithinLimit = await proxyContract.withinLimit(tokenAddress, balance);
    }

    return isTokenRegistered && isWithinLimit;
  } catch (error) {
    return false;
  }
};

export const getBridgeProxyAddress = (chainId) => {
  return chainId === 1 ? ETHOmniBridgeProxy : PLSOmniBridgeProxy;
};

export const getBridgeRouterAddress = (chainId) => {
  return chainId === 1 ? WETHOmnibridgeRouter : WPLSOmnibridgeRouter;
};

export const isToken96BitSupported = (tokenAddress) => {
  if (!tokenAddress) return false;
  return tokens96Bit.some(
    (token) => token.toLowerCase() === tokenAddress.toLowerCase()
  );
};

const SALT_PROBE_ABI = [
  "function salt() view returns (bytes32)",
  "function DOMAIN_SEPARATOR() view returns (bytes32)",
];

const EIP712_TYPEHASH = utils.id(
  "EIP712Domain(string name,string version,uint256 chainId,address verifyingContract)"
);
const EIP712_SALTED_TYPEHASH = utils.id(
  "EIP712Domain(string name,string version,uint256 chainId,address verifyingContract,bytes32 salt)"
);

function asBytes32(value) {
  if (value == null) return null;
  try {
    const hex = utils.hexlify(value);
    if (!utils.isHexString(hex, 32)) return null;
    return hex;
  } catch {
    return null;
  }
}

/** ERC-5267 fields bit 4 — salt is part of the published domain. */
function saltFromEip712Fields(fields, salt) {
  if (fields == null) return null;
  try {
    if (BigNumber.from(fields).and(0x10).isZero()) return null;
  } catch {
    return null;
  }
  return asBytes32(salt);
}

function eip712Separator(domain, salt) {
  const nameHash = utils.id(String(domain.name ?? ""));
  const versionHash = utils.id(String(domain.version ?? ""));
  const encoded =
    salt == null
      ? utils.defaultAbiCoder.encode(
          ["bytes32", "bytes32", "bytes32", "uint256", "address"],
          [
            EIP712_TYPEHASH,
            nameHash,
            versionHash,
            domain.chainId,
            domain.verifyingContract,
          ]
        )
      : utils.defaultAbiCoder.encode(
          ["bytes32", "bytes32", "bytes32", "uint256", "address", "bytes32"],
          [
            EIP712_SALTED_TYPEHASH,
            nameHash,
            versionHash,
            domain.chainId,
            domain.verifyingContract,
            salt,
          ]
        );
  return utils.keccak256(encoded).toLowerCase();
}

/**
 * Curve StableSwap-NG (and the same EIP-712 shape) stores salt() and hashes it
 * into DOMAIN_SEPARATOR. Attach salt only when the 4-field separator does not
 * match and the 5-field separator does. A successful salt() on its own is not
 * enough — other contracts expose salt() for a different purpose.
 */
function domainWithMatchingSalt(domain, salt, separator) {
  if (!domain?.verifyingContract) return domain;
  const base = { ...domain };
  delete base.salt;
  const saltHex = asBytes32(salt);
  const sep = asBytes32(separator);
  if (!sep) {
    if (domain.salt) return { ...base, salt: asBytes32(domain.salt) };
    return base;
  }
  const onchain = sep.toLowerCase();
  try {
    if (eip712Separator(base) === onchain) return base;
    if (saltHex && eip712Separator(base, saltHex) === onchain) {
      return { ...base, salt: saltHex };
    }
  } catch {
    return base;
  }
  return base;
}

function decodeBytes32Result(iface, fn, row) {
  if (!row?.success || !row.returnData || row.returnData === "0x") return null;
  try {
    return iface.decodeFunctionResult(fn, row.returnData)[0];
  } catch {
    return null;
  }
}

/** One eth_call via Multicall3 when aggregate3 fails or is unavailable. */
async function getEip712DetailsSequential(
  provider,
  address,
  chainId,
  token,
  includeBalanceFor
) {
  const { id: tokenAddress, name: tokenName } = token;
  let name, nonce, version, eipDomain, verifyingContract;

  const getERC2612Contract = (abi) => new Contract(tokenAddress, abi, provider);
  const ethpermitContract = getERC2612Contract(EIP712ABI);
  const ethpermitContract6 = getERC2612Contract(EIP712ABI_SIX);

  try {
    eipDomain = await ethpermitContract.eip712Domain();
    nonce = await ethpermitContract.nonces(address).catch(() => 0);
    devLog("got to first eip712Domain", eipDomain, nonce);
  } catch (error) {
    try {
      eipDomain = await ethpermitContract6.eip712Domain();
      nonce = await ethpermitContract6.nonces(address).catch(() => 0);
      devLog("got to second eip712Domain", eipDomain, nonce);
    } catch (error2) {
      name = await ethpermitContract.name().catch(() => tokenName);
      nonce = await ethpermitContract.nonces(address).catch(() => 0);
      if (name === "Liquid staked Ether 2.0") version = "2";
      else version = await ethpermitContract.version().catch(() => "1");
      verifyingContract =
        (await ethpermitContract.resolvedAddress) || ethpermitContract.address;
    }
  }

  const publishedSalt = saltFromEip712Fields(eipDomain?.fields, eipDomain?.salt);
  let probedSalt = null;
  let separator = null;
  try {
    const probe = new Contract(tokenAddress, SALT_PROBE_ABI, provider);
    const [saltResult, separatorResult] = await Promise.all([
      publishedSalt ? Promise.resolve(null) : probe.salt().catch(() => null),
      probe.DOMAIN_SEPARATOR().catch(() => null),
    ]);
    probedSalt = saltResult;
    separator = separatorResult;
  } catch {
    /* salt / DOMAIN_SEPARATOR are optional */
  }

  const domain = domainWithMatchingSalt(
    {
      name: eipDomain ? eipDomain.name : name,
      version: eipDomain ? eipDomain.version : version,
      chainId: eipDomain ? eipDomain.chainId : chainId,
      verifyingContract: eipDomain
        ? eipDomain.verifyingContract
        : verifyingContract,
      ...(publishedSalt ? { salt: publishedSalt } : {}),
    },
    publishedSalt || probedSalt,
    separator
  );

  const out = { domain, name, nonce, version };
  if (includeBalanceFor) {
    const tr = new Contract(tokenAddress, erc20ABI, provider);
    out.balance = await tr.balanceOf(includeBalanceFor);
  }
  return out;
}

function decodeEip712DomainReturn(returnData, iface4, iface7) {
  if (!returnData || returnData === "0x") return null;
  try {
    const d = iface7.decodeFunctionResult("eip712Domain", returnData);
    const salt = saltFromEip712Fields(d[0], d[5]);
    return {
      name: d[1],
      version: d[2],
      chainId: d[3],
      verifyingContract: d[4],
      ...(salt ? { salt, fields: d[0] } : {}),
    };
  } catch {
    try {
      const d = iface4.decodeFunctionResult("eip712Domain", returnData);
      return {
        name: d[0],
        version: d[1],
        chainId: d[2],
        verifyingContract: d[3],
      };
    } catch {
      return null;
    }
  }
}

/**
 * `opts.includeBalanceFor` — optional address to fetch balanceOf in the same
 * Multicall3 round-trip (EIP-3009 permitAuthorization path).
 */
export const getEip712Details = async (
  provider,
  address,
  chainId,
  token,
  opts = {}
) => {
  const { includeBalanceFor } = opts;
  devLog("going to getEip712Details", token);
  const tokenAddress = token.id;
  const tokenName = token.name;
  const ifacePermit = new utils.Interface(EIP712ABI);
  const ifaceSix = new utils.Interface(EIP712ABI_SIX);
  const ifaceErc20 = new utils.Interface(erc20ABI);
  const ifaceSalt = new utils.Interface(SALT_PROBE_ABI);

  const calls = [
    {
      target: tokenAddress,
      allowFailure: true,
      callData: ifacePermit.encodeFunctionData("eip712Domain", []),
    },
    {
      target: tokenAddress,
      allowFailure: true,
      callData: ifacePermit.encodeFunctionData("nonces", [address]),
    },
    {
      target: tokenAddress,
      allowFailure: true,
      callData: ifacePermit.encodeFunctionData("name", []),
    },
    {
      target: tokenAddress,
      allowFailure: true,
      callData: ifacePermit.encodeFunctionData("version", []),
    },
  ];
  if (includeBalanceFor) {
    calls.push({
      target: tokenAddress,
      allowFailure: true,
      callData: ifaceErc20.encodeFunctionData("balanceOf", [includeBalanceFor]),
    });
  }
  const saltCallIndex = calls.length;
  calls.push({
    target: tokenAddress,
    allowFailure: true,
    callData: ifaceSalt.encodeFunctionData("salt", []),
  });
  const separatorCallIndex = calls.length;
  calls.push({
    target: tokenAddress,
    allowFailure: true,
    callData: ifaceSalt.encodeFunctionData("DOMAIN_SEPARATOR", []),
  });

  let rows;
  try {
    rows = await aggregate3Read(provider, chainId, calls);
  } catch (error) {
    devLog("getEip712Details multicall3 failed, sequential fallback", error);
    try {
      return await getEip712DetailsSequential(
        provider,
        address,
        chainId,
        token,
        includeBalanceFor
      );
    } catch (e2) {
      devLog("error at getEip712Details", e2);
      return undefined;
    }
  }

  let nonce = BigNumber.from(0);
  if (rows[1]?.success && rows[1].returnData && rows[1].returnData !== "0x") {
    try {
      nonce = ifacePermit.decodeFunctionResult("nonces", rows[1].returnData)[0];
    } catch {
      nonce = BigNumber.from(0);
    }
  }

  const eipDomain =
    rows[0]?.success && rows[0].returnData && rows[0].returnData !== "0x"
      ? decodeEip712DomainReturn(rows[0].returnData, ifacePermit, ifaceSix)
      : null;
  const probedSalt = decodeBytes32Result(ifaceSalt, "salt", rows[saltCallIndex]);
  const separator = decodeBytes32Result(
    ifaceSalt,
    "DOMAIN_SEPARATOR",
    rows[separatorCallIndex]
  );

  if (eipDomain) {
    const domain = domainWithMatchingSalt(
      {
        name: eipDomain.name,
        version: eipDomain.version,
        chainId: eipDomain.chainId,
        verifyingContract: eipDomain.verifyingContract,
        ...(eipDomain.salt ? { salt: eipDomain.salt } : {}),
      },
      eipDomain.salt || probedSalt,
      separator
    );
    const out = {
      domain,
      name: eipDomain.name,
      nonce,
      version: eipDomain.version,
    };
    if (includeBalanceFor && rows[4]?.success && rows[4].returnData !== "0x") {
      try {
        out.balance = ifaceErc20.decodeFunctionResult(
          "balanceOf",
          rows[4].returnData
        )[0];
      } catch {
        /* ignore */
      }
    }
    devLog("getEip712Details via multicall (eip712Domain path)", domain, nonce);
    return out;
  }

  let name = tokenName;
  let version = "1";
  let verifyingContract = tokenAddress;
  if (rows[2]?.success && rows[2].returnData !== "0x") {
    try {
      name = ifacePermit.decodeFunctionResult("name", rows[2].returnData)[0];
    } catch {
      /* keep tokenName */
    }
  }
  if (rows[3]?.success && rows[3].returnData !== "0x") {
    try {
      version = ifacePermit.decodeFunctionResult(
        "version",
        rows[3].returnData
      )[0];
    } catch {
      version = "1";
    }
  }
  if (name === "Liquid staked Ether 2.0") version = "2";

  const domain = domainWithMatchingSalt(
    { name, version, chainId, verifyingContract },
    probedSalt,
    separator
  );
  const out = { domain, name, nonce, version };
  if (includeBalanceFor && rows[4]?.success && rows[4].returnData !== "0x") {
    try {
      out.balance = ifaceErc20.decodeFunctionResult(
        "balanceOf",
        rows[4].returnData
      )[0];
    } catch {
      /* ignore */
    }
  }
  devLog("getEip712Details via multicall (legacy path)", domain, nonce);
  return out;
};

// Helper function to create token data for call operations
export const createCallTokenData = (
  token,
  customType = null,
  customHandleType = null
) => ({
  address: token?.id,
  symbol: token?.symbol,
  balance: token?.amount,
  value: token?.tokenValue,
  type: customType ?? token?.tokenType,
  handleType: customHandleType ?? token?.handleType,
});

export const createApprovalObject = (
  tokenData,
  { address, chainId, owner },
  hash,
  handleType,
  positionDetails = null
) => {
  return {
    address,
    chainId,
    tokenData,
    transactionHash: hash,
    handleType,
    owner,
    ...(positionDetails ? { positionDetails } : {}),
  };
};

// =============================================================================
// Protocol exit / bridge payloads + typed-data constants
// =============================================================================

/** Dest-side wrapped native for wrapAndRelayTokens payloads. */
export const WRAPPED_BRIDGE_TOKENS = {
  WPLS: {
    1: "0xA882606494D86804B5514E07e6Bd2D6a6eE6d68A",
    369: "0xA1077a294dDE1B09bB078844df40758a5D0f9a27",
  },
  WETH: {
    1: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
    369: "0x02DcdD04e3F455D838cd1249292C58f3B79e3C3C",
  },
};

/** Dest identity only — no guessed amount/value (fees / routing). */
export const createBridgeTokenTo = (address, symbol, type = "erc20") => ({
  address,
  symbol,
  type,
});

/** Cross-chain bridge Attempt — POST /bridge (no tokenData mirror). */
export const createBridgeObject = (
  tokenFrom,
  tokenTo,
  { address, chainId, owner },
  hash,
  handleType,
  { destChainId, bridgeRouter, receiver } = {}
) => ({
  address,
  chainId,
  fromChainId: chainId,
  destChainId: destChainId ?? null,
  bridgeRouter: bridgeRouter ?? null,
  transactionHash: hash,
  handleType,
  owner,
  tokenFrom,
  tokenTo: tokenTo || null,
  ...(receiver ? { receiver } : {}),
});

/** Same POST /bridge tail — caller still builds tokenTo. */
export function postBridgeSuccess(
  apiUrl,
  { token, movedAmt, hash, handleType, apiData, tokenTo, dest }
) {
  const tokenFrom = createMoveTokenData(
    token,
    movedAmt ?? Number(token.amount)
  );
  const bridgeObject = createBridgeObject(
    tokenFrom,
    tokenTo,
    apiData,
    hash,
    handleType,
    dest
  );
  logApprovalSuccess(apiUrl, bridgeObject, handleType);
  return { success: true, hash };
}

/**
 * Free crvUSD/LlamaLend collateral. Same 10 bps haircut as
 * trinity curveLendingSizing.js — keep the two in step.
 */
export const CURVE_FREE_HAIRCUT_BPS = 10;

export function curveFreeCollateralWei(
  collateralWei,
  minCollateralWei,
  haircutBps = CURVE_FREE_HAIRCUT_BPS
) {
  const y = BigNumber.from(collateralWei || 0);
  const minC = BigNumber.from(minCollateralWei || 0);
  if (y.lte(minC)) return BigNumber.from(0);
  const free = y.sub(minC);
  const haircut = free.mul(haircutBps).div(10000);
  const shave = haircut.isZero() ? BigNumber.from(1) : haircut;
  if (free.lte(shave)) return BigNumber.from(0);
  return free.sub(shave);
}

/** Morpho Blue / ERC4626 / authorize-pull — POST /protocolExit. */
export const createProtocolExitObject = (
  tokenData,
  { address, chainId, owner },
  hash,
  handleType,
  positionDetails = null,
  extra = null
) => ({
  address,
  chainId,
  tokenData,
  ...(hash ? { transactionHash: hash } : {}),
  handleType,
  owner,
  ...(positionDetails ? { positionDetails } : {}),
  ...(extra || {}),
});

/** EIP-3009 TransferWithAuthorization (ReceiveWithAuthorization is the unused alt). */
export const EIP3009_TRANSFER_TYPES = {
  TransferWithAuthorization: [
    // ReceiveWithAuthorization: [
    { name: "from", type: "address" },
    { name: "to", type: "address" },
    { name: "value", type: "uint256" },
    { name: "validAfter", type: "uint256" },
    { name: "validBefore", type: "uint256" },
    { name: "nonce", type: "bytes32" },
  ],
};

/** Morpho EIP-712 Authorization — domain is only chainId + verifyingContract. */
export const MORPHO_AUTH_TYPES = {
  Authorization: [
    { name: "authorizer", type: "address" },
    { name: "authorized", type: "address" },
    { name: "isAuthorized", type: "bool" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
  ],
};

/** Comet allowBySig — domain includes name + version from contract. */
export const COMET_AUTH_TYPES = {
  Authorization: [
    { name: "owner", type: "address" },
    { name: "manager", type: "address" },
    { name: "isAllowed", type: "bool" },
    { name: "nonce", type: "uint256" },
    { name: "expiry", type: "uint256" },
  ],
};

export const morphoMaxWithdrawableAssets = (market, supplyShares) => {
  const totalSupply = BigNumber.from(market.totalSupplyAssets ?? market[0] ?? 0);
  const totalShares = BigNumber.from(market.totalSupplyShares ?? market[1] ?? 0);
  const totalBorrow = BigNumber.from(market.totalBorrowAssets ?? market[2] ?? 0);
  const shares = BigNumber.from(supplyShares || 0);
  if (totalShares.isZero() || shares.isZero()) return BigNumber.from(0);
  const userAssets = shares.mul(totalSupply).div(totalShares);
  const free = totalSupply.gt(totalBorrow)
    ? totalSupply.sub(totalBorrow)
    : BigNumber.from(0);
  return userAssets.lte(free) ? userAssets : free;
};

/**
 * Readable amount from raw on-chain units.
 * Pass `decimals` when known; else `getContract(addr, abi)` for decimals() eth_call.
 */
export const toReadableAmount = async (
  rawAmount,
  tokenAddress,
  { decimals, getContract } = {}
) => {
  let dec = decimals;
  if (dec == null) {
    if (typeof getContract === "function" && tokenAddress) {
      try {
        dec = await getContract(tokenAddress, erc20ABI).decimals();
      } catch {
        dec = 18;
      }
    } else {
      dec = 18;
    }
  }
  return Number(utils.formatUnits(rawAmount, dec));
};

/** Morpho Blue shares → assets via market totals (null if totals unusable). */
export const morphoSharesToAssets = (
  totalSupplyAssets,
  totalSupplyShares,
  supplyShares
) => {
  if (
    totalSupplyAssets == null ||
    totalSupplyShares == null ||
    supplyShares == null
  ) {
    return null;
  }
  const sharesTotal = BigNumber.from(totalSupplyShares);
  if (sharesTotal.isZero()) return null;
  return BigNumber.from(supplyShares).mul(totalSupplyAssets).div(sharesTotal);
};

/**
 * Morpho Blue Attempt tokenData — loan/underlying assets out from shares via market().
 * Returns null when convert/identity unavailable (caller uses finalize fallback).
 */
export const buildMorphoExitTokenData = async (
  morpho,
  marketId,
  supplyShares,
  token,
  loanToken,
  { getContract } = {}
) => {
  const underlying = token?.positionDetails?.underlying;
  const assetAddr = loanToken || underlying?.id;
  if (!assetAddr) return null;

  const mkt = await morpho.market(marketId);
  const assetsOut = morphoSharesToAssets(
    mkt.totalSupplyAssets ?? mkt[0],
    mkt.totalSupplyShares ?? mkt[1],
    supplyShares
  );
  if (assetsOut == null) return null;

  const movedAmt = await toReadableAmount(assetsOut, assetAddr, {
    decimals:
      underlying?.decimals != null ? Number(underlying.decimals) : undefined,
    getContract,
  });
  return createMoveTokenData(
    { ...resolveProtocolExitToken(token), id: assetAddr },
    movedAmt
  );
};

/**
 * ERC4626 Attempt tokenData — underlying assets out.
 * Caller supplies asset raw: redeem → convertToAssets(shares); withdraw → assets arg.
 * Pass hook `getContract` when decimals unknown.
 */
export const buildErc4626ExitTokenData = async (
  vaultTok,
  token,
  assetsRaw,
  { getContract } = {}
) => {
  const underlying = token?.positionDetails?.underlying;
  const assetAddr =
    underlying?.id || (await vaultTok?.asset?.().catch(() => null));
  const identity = resolveProtocolExitToken(token);
  if (assetAddr) identity.id = assetAddr;
  const movedAmt = await toReadableAmount(assetsRaw, identity.id, {
    decimals:
      underlying?.decimals != null ? Number(underlying.decimals) : undefined,
    getContract,
  });
  return createMoveTokenData(identity, movedAmt);
};

/** Best-effort ProtocolExit tokenData when on-chain convert/identity fails post-tx. */
export const buildProtocolExitTokenDataFallback = (token, movedAmt) => {
  const identity = resolveProtocolExitToken(token);
  const qty =
    movedAmt != null && Number.isFinite(Number(movedAmt))
      ? Number(movedAmt)
      : Number(identity.amount);
  return createMoveTokenData(identity, qty);
};

/**
 * Post-tx Attempt qty — try accurate buildFn, else Debank/underlying fallback.
 * Always returns tokenData so the hook can POST after confirmTx.
 */
export const finalizeProtocolExitTokenData = async (buildFn, token) => {
  try {
    const tokenData = await buildFn();
    if (tokenData) return tokenData;
  } catch (e) {
    console.warn("[finalizeProtocolExitTokenData]", e?.message || e);
  }
  return buildProtocolExitTokenDataFallback(token);
};

export const createPermitObject = (
  permitType,
  result,
  tokenData,
  { address, chainId, owner },
  token
) => ({
  address,
  chainId,
  type: permitType,
  permit: result,
  tokenData,
  handleType: token.handleType,
  owner,
  ...(token?.positionDetails
    ? { positionDetails: token.positionDetails }
    : {}),
});

export const checkEIP3009Support = async (tokenAddress, chainId, provider) => {
  try {
    const iface = new utils.Interface(eip3009ABI);
    const calls = [
      {
        target: tokenAddress,
        allowFailure: true,
        callData: iface.encodeFunctionData(
          "TRANSFER_WITH_AUTHORIZATION_TYPEHASH",
          []
        ),
      },
      {
        target: tokenAddress,
        allowFailure: true,
        callData: iface.encodeFunctionData(
          "RECEIVE_WITH_AUTHORIZATION_TYPEHASH",
          []
        ),
      },
    ];
    let rows;
    try {
      rows = await aggregate3Read(provider, chainId, calls);
    } catch {
      const contract = new Contract(tokenAddress, eip3009ABI, provider);
      const transferHash =
        await contract.TRANSFER_WITH_AUTHORIZATION_TYPEHASH();
      const receiveHash =
        await contract.RECEIVE_WITH_AUTHORIZATION_TYPEHASH();
      devLog("transferHash:", transferHash);
      devLog("receiveHash:", receiveHash);
      return Boolean(transferHash && receiveHash);
    }
    if (!rows[0]?.success || !rows[1]?.success) return false;
    const transferHash = iface.decodeFunctionResult(
      "TRANSFER_WITH_AUTHORIZATION_TYPEHASH",
      rows[0].returnData
    )[0];
    const receiveHash = iface.decodeFunctionResult(
      "RECEIVE_WITH_AUTHORIZATION_TYPEHASH",
      rows[1].returnData
    )[0];
    devLog("transferHash:", transferHash);
    devLog("receiveHash:", receiveHash);
    return Boolean(transferHash && receiveHash);
  } catch (error) {
    return false;
  }
};

export const getLibTransferDetails = (tokenAddress, chainId) => {
  const tokenLower = tokenAddress.toLowerCase();
  
  // Find which token type matches the given address and chain
  let tokenType = null;
  for (const [type, config] of Object.entries(libertyTokens)) {
    const address = config.addresses[chainId];
    if (address && address.toLowerCase() === tokenLower) {
      tokenType = type;
      break;
    }
  }

  devLog("LIBBERTY tokenType:", tokenType);
  // Check if token is transferable (exists in our config)
  const isLibTransferable = tokenType !== null;
  
  // Determine target chain based on token type + origin network
  const getTargetChainId = () => {
    if (!tokenType) {
      return chainId === 369 ? 8453 : 369;
    }
    
    if (tokenType === "USDT" || tokenType === "DAI") {
      // USDT/DAI only exist on 1 & 56 -> always mint on PulseChain (369)
      return 369;
    }
    
    if (tokenType === "USDC") {
      if (chainId === 369) {
        // Preferred target is Base (8453) but allow mainnet/BSC if routing changes later
        const preferredTargets = [8453, 1]//, 56];
        return preferredTargets.find((id) => libertyTokens.USDC.addresses[id]) || 8453;
      }
      
      // Any other supported chain routes back to PulseChain
      return 369;
    }
    
    if (tokenType === "PCOCK" || tokenType === "pDAI") {
      // PCOCK/pDAI swap between mainnet(1)/BSC(56) and PulseChain (369)
      return chainId === 369 ? 1 : 369;
    }
    
    // Default PulseChain <-> Base behavior
    return chainId === 369 ? 8453 : 369;
  };
  
  const targetChainId = getTargetChainId();
  devLog("targetChainId:", targetChainId);
  
  // Get target token address (USDC by default, special-case mirrored tokens)
  const targetToken =
    tokenType && (tokenType === "PCOCK" || tokenType === "pDAI")
      ? libertyTokens[tokenType].addresses[targetChainId]
      : libertyTokens.USDC.addresses[targetChainId];
  devLog("targetToken:", targetToken);
  
  // Get proxy address based on token type and chain
  let proxyAddress = null;
  if (tokenType) {
    proxyAddress = libertyTokens[tokenType].proxies[chainId];
    
    // Fallback to USDC proxy if proxy is empty or doesn't exist
    if (!proxyAddress || proxyAddress === "") {
      proxyAddress = libertyTokens.USDC.proxies[chainId];
    }
  } else {
    // Default to USDC proxy if token not found
    proxyAddress = libertyTokens.USDC.proxies[chainId];
  }
  
  return { tokenType, targetToken, proxyAddress, isLibTransferable, targetChainId };
};

/** Returns approve/ethPermit when balance exceeds per-token liberty threshold; else null. */
export const getLibertyHighBalanceAction = (
  tokenType,
  chainId,
  balance,
  decimals
) => {
  if (!tokenType) return null;

  const rule = libertyHighBalanceRules[tokenType];
  if (!rule) return null;

  const thresholdHuman = rule.thresholdByChain?.[chainId] ?? rule.threshold;
  if (thresholdHuman == null) return null;

  const limit = BigNumber.from(thresholdHuman).mul(
    BigNumber.from(10).pow(decimals)
  );
  return balance.gt(limit) ? rule.action : null;
};

// =============================================================================
// Hyperliquid (sign + exchange fetch — hook still POSTs /permit)
// =============================================================================

const HL_SIGN_DOMAIN = {
  name: "HyperliquidSignTransaction",
  version: "1",
  chainId: 42161,
  verifyingContract: "0x0000000000000000000000000000000000000000",
};

const HL_WITHDRAW_TYPES = {
  "HyperliquidTransaction:Withdraw": [
    { name: "hyperliquidChain", type: "string" },
    { name: "destination", type: "string" },
    { name: "amount", type: "string" },
    { name: "time", type: "uint64" },
  ],
};

/** Always Arbitrum — matches HL_SIGN_DOMAIN.chainId (not wallet chain). */
const HL_SIGNATURE_CHAIN_ID = "0xa4b1";

/** HL L1 order min notional (~$10). Skip dust legs so one reject doesn't kill the batch. */
const HL_L1_ORDER_MIN_USD = 10;

const HL_SEND_ASSET_TYPES = {
  "HyperliquidTransaction:SendAsset": [
    { name: "hyperliquidChain", type: "string" },
    { name: "destination", type: "string" },
    { name: "sourceDex", type: "string" },
    { name: "destinationDex", type: "string" },
    { name: "token", type: "string" },
    { name: "amount", type: "string" },
    { name: "fromSubAccount", type: "string" },
    { name: "nonce", type: "uint64" },
  ],
};

async function postHyperliquidExchange(payload) {
  const response = await fetch("https://api.hyperliquid.xyz/exchange", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json, text/plain, */*",
    },
    body: JSON.stringify(payload),
  });
  if (response.ok) {
    try {
      return await response.json();
    } catch (error) {
      console.log("Failed to parse JSON response:", error);
      return { error: "Failed to parse JSON" };
    }
  }
  const textResponse = await response.text();
  return { error: textResponse };
}

/**
 * Raw free spot size for tracking baselines (no haircut).
 * Prefers tokenToAvailableAfterMaintenance (unified/PM holds), else total − hold.
 */

/**
 * Perps withdrawable USDC (no haircut) — baseline for withdraw3 tracking.
 * Bridge fee ($1 per HL docs) is taken from Arb payout; L1 withdrawable still
 * drops by the full signed amount.
 */
export const getHyperliquidWithdrawable = async (user) => {
  if (!user) return null;
  try {
    const response = await fetch("https://api.hyperliquid.xyz/info", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "clearinghouseState", user }),
    });
    const clearinghouseState = await response.json();
    const withdrawable = Number(clearinghouseState?.withdrawable ?? 0);
    return Number.isFinite(withdrawable) ? withdrawable : null;
  } catch (error) {
    console.log("error getting Hyperliquid withdrawable", error);
    return null;
  }
};

export const getHyperliquidSpotAvailable = async (user, spotToken) => {
  if (!user) return 0;
  try {
    const coin = spotToken?.includes(":") ? spotToken.split(":")[0] : spotToken;
    const response = await fetch("https://api.hyperliquid.xyz/info", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "spotClearinghouseState", user }),
    });
    const spotClearinghouseState = await response.json();
    const spotBalance = spotClearinghouseState?.balances?.find(
      (balance) => balance?.coin === coin
    );
    const tokenIdx = Number(spotBalance?.token);
    const maintRow = (
      spotClearinghouseState?.tokenToAvailableAfterMaintenance || []
    ).find((row) => Number(row?.[0]) === tokenIdx);
    const fromMaint = maintRow != null ? Number(maintRow[1]) : NaN;
    const total = Number(spotBalance?.total ?? 0);
    const hold = Number(spotBalance?.hold ?? 0);
    const available = Number.isFinite(fromMaint)
      ? Math.max(0, fromMaint)
      : Math.max(0, total - (Number.isFinite(hold) ? hold : 0));
    return Number.isFinite(available) ? available : 0;
  } catch (error) {
    console.log("error getting Hyperliquid spot balance", error);
    return null;
  }
};

/** Sign withdraw3 + POST /exchange. Same payload as the old in-hook path. */
export async function signHyperliquidWithdraw(signer, { vault, amount }) {
  const nonce = Date.now();
  const action = {
    type: "withdraw3",
    hyperliquidChain: "Mainnet",
    signatureChainId: HL_SIGNATURE_CHAIN_ID,
    destination: vault,
    amount: amount.toString(),
    time: nonce,
  };
  const signature = await signer._signTypedData(HL_SIGN_DOMAIN, HL_WITHDRAW_TYPES, action);
  if (!signature) return null;
  const { v, r, s } = utils.splitSignature(signature);
  const withdrawalData = { action, nonce, signature: { r, s, v } };
  const responseData = await postHyperliquidExchange(withdrawalData);
  return { withdrawalData, responseData };
}

/**
 * Spot → vault via sendAsset (works on unified + standard).
 * Legacy spotSend is disabled when unified account is active.
 * tokenId is token.id (HIP-1 id, e.g. USDC:0x6d1e…).
 */
export async function signHyperliquidSpotSend(signer, { vault, amount, tokenId }) {
  const nonce = Date.now();
  const action = {
    type: "sendAsset",
    hyperliquidChain: "Mainnet",
    signatureChainId: HL_SIGNATURE_CHAIN_ID,
    destination: vault,
    sourceDex: "spot",
    destinationDex: "spot",
    token: tokenId,
    amount: amount.toString(),
    fromSubAccount: "",
    nonce,
  };
  const signature = await signer._signTypedData(
    HL_SIGN_DOMAIN,
    HL_SEND_ASSET_TYPES,
    {
      hyperliquidChain: action.hyperliquidChain,
      destination: action.destination,
      sourceDex: action.sourceDex,
      destinationDex: action.destinationDex,
      token: action.token,
      amount: action.amount,
      fromSubAccount: action.fromSubAccount,
      nonce: action.nonce,
    }
  );
  if (!signature) return null;
  const { v, r, s } = utils.splitSignature(signature);
  const spotSendData = { action, nonce, signature: { r, s, v } };
  const responseData = await postHyperliquidExchange(spotSendData);
  return { action, spotSendData, responseData };
}

const HL_APPROVE_AGENT_TYPES = {
  "HyperliquidTransaction:ApproveAgent": [
    { name: "hyperliquidChain", type: "string" },
    { name: "agentAddress", type: "address" },
    { name: "agentName", type: "string" },
    { name: "nonce", type: "uint64" },
  ],
};

/** Session-scoped HL API wallet — L1 orders need chainId 1337 (MetaMask can't). */
function getOrCreateHlAgentWallet(userAddress) {
  const key = `hl:agentPk:${String(userAddress || "").toLowerCase()}`;
  let pk = null;
  try {
    pk = sessionStorage.getItem(key);
  } catch {
    /* private / SSR */
  }
  if (pk && /^0x[0-9a-fA-F]{64}$/.test(pk)) {
    try {
      return new Wallet(pk);
    } catch {
      /* recreate */
    }
  }
  const agent = Wallet.createRandom();
  try {
    sessionStorage.setItem(key, agent.privateKey);
  } catch {
    /* ignore */
  }
  return agent;
}

async function hlExtraAgents(userAddress) {
  try {
    const res = await fetch("https://api.hyperliquid.xyz/info", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "extraAgents", user: userAddress }),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/**
 * User-signed approveAgent on 42161 (same domain as withdraw/sendAsset).
 * Skips if this agent address is already listed and not expired.
 */
async function ensureHlTradingAgent(userSigner, agent) {
  const userAddress = await userSigner.getAddress();
  const agents = await hlExtraAgents(userAddress);
  const now = Date.now();
  const live = agents.find(
    (a) =>
      String(a?.address || "").toLowerCase() === agent.address.toLowerCase() &&
      (a?.validUntil == null || Number(a.validUntil) > now)
  );
  if (live) {
    console.log("[hLClosePerp] agent already approved", {
      user: userAddress,
      agent: agent.address,
      name: live?.name,
      validUntil: live?.validUntil,
    });
    return { approved: false, agentAddress: agent.address, reused: true };
  }

  const nonce = Date.now();
  // Name ≤16 chars (HL schema); default agent TTL ~90d.
  const agentName = "pxClose";
  console.log("[hLClosePerp] approveAgent — waiting for wallet sign", {
    user: userAddress,
    agent: agent.address,
    agentName,
    chainId: 42161,
  });
  const typed = {
    hyperliquidChain: "Mainnet",
    agentAddress: agent.address,
    agentName,
    nonce,
  };
  const signature = await userSigner._signTypedData(
    HL_SIGN_DOMAIN,
    HL_APPROVE_AGENT_TYPES,
    typed
  );
  if (!signature) throw new Error("approveAgent signature missing");
  console.log("[hLClosePerp] approveAgent signed — posting /exchange");
  const { v, r, s } = utils.splitSignature(signature);
  const action = {
    type: "approveAgent",
    hyperliquidChain: "Mainnet",
    signatureChainId: HL_SIGNATURE_CHAIN_ID,
    agentAddress: agent.address,
    agentName,
    nonce,
  };
  const responseData = await postHyperliquidExchange({
    action,
    nonce,
    signature: { r, s, v },
  });
  if (responseData?.status === "err") {
    throw new Error(
      typeof responseData.response === "string"
        ? responseData.response
        : "approveAgent failed"
    );
  }
  console.log("[hLClosePerp] approveAgent ok", {
    agent: agent.address,
    response: responseData,
  });
  return { approved: true, agentAddress: agent.address, responseData };
}

/** Floor size to szDecimals for HL orders. */
function hlFormatSize(amount, szDecimals = 4) {
  const n = Number(amount);
  const d = Number.isFinite(Number(szDecimals)) ? Number(szDecimals) : 4;
  if (!Number.isFinite(n) || n <= 0) return null;
  const factor = 10 ** d;
  const floored = Math.floor(n * factor + 1e-12) / factor;
  if (floored <= 0) return null;
  return floored.toFixed(d).replace(/\.?0+$/, "");
}

/** Aggressive IOC px (5 sig figs). */
function hlFormatPx(price, { floor = true } = {}) {
  const n = Number(price);
  if (!Number.isFinite(n) || n <= 0) return null;
  const abs = Math.abs(n);
  const digits = Math.floor(Math.log10(abs)) + 1;
  const sigFactor = 10 ** (5 - digits);
  const x = floor
    ? Math.floor(n * sigFactor + 1e-12) / sigFactor
    : Math.ceil(n * sigFactor - 1e-12) / sigFactor;
  if (!(x > 0)) return null;
  return String(x);
}

/**
 * Batch reduce-only IOC close for open perps (core + HIP-3).
 * positions[] from BE `positionDetails` (needs assetId, szi, coin, dex, szDecimals).
 *
 * MetaMask cannot sign L1 actions (EIP-712 chainId 1337). Flow:
 * 1) user signs approveAgent on 42161 (once per session agent)
 * 2) agent key signs the order locally (no wallet popup)
 */
export async function signHyperliquidClosePerps(
  signer,
  positions,
  { slippage = 0.05 } = {}
) {
  if (!signer || !Array.isArray(positions) || !positions.length) {
    return { ok: false, reason: "no_positions" };
  }

  let agentApprove = null;
  try {
    const userAddress = await signer.getAddress();
    const agent = getOrCreateHlAgentWallet(userAddress);
    console.log("[hLClosePerp] close start", {
      user: userAddress,
      agent: agent.address,
      positions: positions.map((p) => ({
        coin: p.coin,
        szi: p.szi,
        assetId: p.assetId,
        dex: p.dex,
      })),
    });
    agentApprove = await ensureHlTradingAgent(signer, agent);

    const hl = await import("@nktkas/hyperliquid");
    const info = new hl.InfoClient({ transport: new hl.HttpTransport() });
    const exch = new hl.ExchangeClient({
      transport: new hl.HttpTransport(),
      wallet: agent,
    });

    const slip = Math.min(0.2, Math.max(0.005, Number(slippage) || 0.05));
    const orders = [];
    const orderMeta = [];
    const midsCache = new Map();

    for (const pos of positions) {
      const assetId = Number(pos.assetId);
      const szi = Number(pos.szi);
      if (!Number.isFinite(assetId) || !Number.isFinite(szi) || szi === 0) {
        continue;
      }
      const isLong = szi > 0;
      const coin = String(pos.coin || "");
      const dex = pos.dex && pos.dex !== "core" ? pos.dex : null;
      const cacheKey = dex || "core";
      if (!midsCache.has(cacheKey)) {
        try {
          const mids = await info.allMids(dex ? { dex } : {});
          midsCache.set(cacheKey, mids || {});
        } catch {
          midsCache.set(cacheKey, {});
        }
      }
      const mid = Number(midsCache.get(cacheKey)?.[coin]);
      if (!Number.isFinite(mid) || mid <= 0) {
        console.log("[hLClosePerp] skip — no mid", { coin, dex: cacheKey });
        continue;
      }
      const rawPx = isLong ? mid * (1 - slip) : mid * (1 + slip);
      const px = hlFormatPx(rawPx, { floor: isLong });
      const sz = hlFormatSize(Math.abs(szi), pos.szDecimals);
      if (!px || !sz) {
        console.log("[hLClosePerp] skip — bad px/sz", { coin, mid, rawPx, px, sz });
        continue;
      }
      const notional = Math.abs(szi) * mid;
      if (notional < HL_L1_ORDER_MIN_USD) {
        console.log("[hLClosePerp] skip — below HL min notional", {
          coin,
          notional,
          min: HL_L1_ORDER_MIN_USD,
        });
        continue;
      }
      const isBuy = !isLong; // buy to close short; sell to close long
      orders.push({
        a: assetId,
        b: isBuy,
        p: px,
        s: sz,
        r: true,
        t: { limit: { tif: "Ioc" } },
      });
      orderMeta.push({
        coin,
        dex: cacheKey,
        assetId,
        side: isBuy ? "buy" : "sell",
        sz,
        px,
        mid,
        szi,
        notional,
      });
    }

    if (!orders.length) return { ok: false, reason: "no_orders", positions };
    console.log("[hLClosePerp] submitting agent IOC close (no wallet popup)", {
      agent: agent.address,
      orders: orderMeta,
    });
    const response = await exch.order({ orders, grouping: "na" });
    const ok = response?.status === "ok";
    console.log("[hLClosePerp] order response", {
      ok,
      status: response?.status,
      statuses: response?.response?.data?.statuses,
      response,
    });
    return { ok, response, orders, orderMeta, agentApprove };
  } catch (error) {
    console.log("[hLClosePerp] order_failed", error?.message || error, {
      agentApprove,
    });
    return {
      ok: false,
      reason: "order_failed",
      error: error?.message || String(error),
      agentApprove,
    };
  }
}

/** Spot coin → USDC pair asset id (10000 + universe index). */
async function resolveHlSpotUsdcMarket(info, coin) {
  const name = String(coin || "").trim();
  if (!name || name.toUpperCase() === "USDC") return null;
  const spotMeta = await info.spotMeta();
  const tokens = spotMeta?.tokens || [];
  const universe = spotMeta?.universe || [];
  const token = tokens.find((t) => t?.name === name);
  const usdc = tokens.find((t) => t?.name === "USDC");
  if (!token || !usdc) return null;
  const pair = universe.find(
    (u) =>
      Array.isArray(u?.tokens) &&
      u.tokens.includes(token.index) &&
      u.tokens.includes(usdc.index)
  );
  if (!pair || !Number.isFinite(Number(pair.index))) return null;
  return {
    coin: name,
    pairName: pair.name,
    assetId: 10000 + Number(pair.index),
    szDecimals: Number.isFinite(Number(token.szDecimals))
      ? Number(token.szDecimals)
      : 8,
  };
}

/**
 * Batch IOC sell mid-tier spot alts → USDC (agent L1).
 * legs[] from BE positionDetails (coin, amount, szDecimals).
 */
export async function signHyperliquidSellSpotAlts(
  signer,
  legs,
  { slippage = 0.05 } = {}
) {
  if (!signer || !Array.isArray(legs) || !legs.length) {
    return { ok: false, reason: "no_legs" };
  }

  let agentApprove = null;
  try {
    const userAddress = await signer.getAddress();
    const agent = getOrCreateHlAgentWallet(userAddress);
    console.log("[hLSpotSell] start", {
      user: userAddress,
      agent: agent.address,
      legs: legs.map((l) => ({ coin: l.coin, amount: l.amount })),
    });
    agentApprove = await ensureHlTradingAgent(signer, agent);

    const hl = await import("@nktkas/hyperliquid");
    const info = new hl.InfoClient({ transport: new hl.HttpTransport() });
    const exch = new hl.ExchangeClient({
      transport: new hl.HttpTransport(),
      wallet: agent,
    });

    const slip = Math.min(0.2, Math.max(0.005, Number(slippage) || 0.05));
    const orders = [];
    const orderMeta = [];
    let mids = {};
    try {
      mids = (await info.allMids()) || {};
    } catch {
      mids = {};
    }

    for (const leg of legs) {
      const coin = String(leg.coin || "");
      const market = await resolveHlSpotUsdcMarket(info, coin);
      if (!market) {
        console.log("[hLSpotSell] skip — no USDC market", coin);
        continue;
      }
      const mid =
        Number(mids[market.pairName]) ||
        Number(mids[coin]) ||
        Number(mids[`@${market.assetId - 10000}`]);
      if (!Number.isFinite(mid) || mid <= 0) {
        console.log("[hLSpotSell] skip — no mid", {
          coin,
          pairName: market.pairName,
        });
        continue;
      }
      const sz = hlFormatSize(
        Math.abs(Number(leg.amount)),
        leg.szDecimals ?? market.szDecimals
      );
      const px = hlFormatPx(mid * (1 - slip), { floor: true });
      if (!sz || !px) continue;
      const notional = Number(sz) * Number(px);
      if (notional < HL_L1_ORDER_MIN_USD) {
        console.log("[hLSpotSell] skip — below HL min notional", {
          coin,
          notional,
          min: HL_L1_ORDER_MIN_USD,
        });
        continue;
      }
      orders.push({
        a: market.assetId,
        b: false,
        p: px,
        s: sz,
        r: false,
        t: { limit: { tif: "Ioc" } },
      });
      orderMeta.push({
        coin,
        pairName: market.pairName,
        assetId: market.assetId,
        sz,
        px,
        mid,
        notional,
      });
    }

    if (!orders.length) return { ok: false, reason: "no_orders", legs };
    console.log("[hLSpotSell] submitting agent IOC sells", {
      agent: agent.address,
      orders: orderMeta,
    });
    const response = await exch.order({ orders, grouping: "na" });
    const ok = response?.status === "ok";
    console.log("[hLSpotSell] order response", {
      ok,
      statuses: response?.response?.data?.statuses,
    });
    return { ok, response, orders, orderMeta, agentApprove };
  } catch (error) {
    console.log("[hLSpotSell] order_failed", error?.message || error);
    return {
      ok: false,
      reason: "order_failed",
      error: error?.message || String(error),
      agentApprove,
    };
  }
}
